import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Button, Card, CardContent, Grid, TextField, Alert } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReplayIcon from '@mui/icons-material/Replay';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useNavigate } from 'react-router-dom';
import api from '../../api';

// Utility to shuffle an array
const shuffleArray = (array: any[]) => {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const VocabularyGames: React.FC = () => {
  const navigate = useNavigate();
  const [activeGame, setActiveGame] = useState<string | null>(null);
  const [wordsPool, setWordsPool] = useState<any[]>([]);

  // General state
  const [score, setScore] = useState(0);
  const [gameMsg, setGameMsg] = useState<{ text: string; severity: 'success' | 'error' | 'info' } | null>(null);

  // Word Match Game State
  const [matchQuestions, setMatchQuestions] = useState<any[]>([]);
  const [matchAnswers, setMatchAnswers] = useState<any[]>([]);
  const [selectedWord, setSelectedWord] = useState<string | null>(null);
  const [selectedDef, setSelectedDef] = useState<string | null>(null);
  const [matchedPairs, setMatchedPairs] = useState<string[]>([]); // words that are successfully matched

  // Word Scramble Game State
  const [scrambleWord, setScrambleWord] = useState<any | null>(null);
  const [scrambledText, setScrambledText] = useState('');
  const [scrambleGuess, setScrambleGuess] = useState('');

  // Hangman Game State
  const [hangmanWord, setHangmanWord] = useState<string>('');
  const [hangmanClue, setHangmanClue] = useState<string>('');
  const [guessedLetters, setGuessedLetters] = useState<string[]>([]);
  const [wrongGuessesCount, setWrongGuessesCount] = useState(0);
  const maxWrongGuesses = 6;

  // Fetch words pool for games from Dictionary
  const fetchWordsPool = async () => {
    try {
      const res = await api.get('/language-hub?type=dictionary&limit=100');
      if (res.data && res.data.success) {
        setWordsPool(res.data.data);
      }
    } catch (e) {
      console.error('Error fetching words for games:', e);
    }
  };

  useEffect(() => {
    fetchWordsPool();
  }, []);

  // --- Word Match Game Functions ---
  const startWordMatch = () => {
    if (wordsPool.length < 5) return;
    const selected = shuffleArray(wordsPool).slice(0, 5);
    setMatchQuestions(shuffleArray(selected));
    setMatchAnswers(shuffleArray(selected));
    setMatchedPairs([]);
    setSelectedWord(null);
    setSelectedDef(null);
    setGameMsg(null);
    setScore(0);
    setActiveGame('match');
  };

  const handleMatchWordClick = (word: string) => {
    if (matchedPairs.includes(word)) return;
    setSelectedWord(word);
    if (selectedDef) {
      checkMatch(word, selectedDef);
    }
  };

  const handleMatchDefClick = (def: string) => {
    // find word that has this definition
    const matchObj = matchQuestions.find(q => q.definition === def);
    if (!matchObj || matchedPairs.includes(matchObj.word)) return;
    setSelectedDef(def);
    if (selectedWord) {
      checkMatch(selectedWord, def);
    }
  };

  const checkMatch = (word: string, def: string) => {
    const correctPair = matchQuestions.find(q => q.word === word && q.definition === def);
    if (correctPair) {
      setMatchedPairs(prev => [...prev, word]);
      setScore(prev => prev + 10);
      setGameMsg({ text: 'Correct Match! +10 points', severity: 'success' });
      if (matchedPairs.length + 1 === 5) {
        setGameMsg({ text: 'Congratulations! You matched all words correctly!', severity: 'success' });
      }
    } else {
      setGameMsg({ text: 'Incorrect match. Try again!', severity: 'error' });
    }
    setSelectedWord(null);
    setSelectedDef(null);
  };

  // --- Word Scramble Game Functions ---
  const startWordScramble = () => {
    if (wordsPool.length === 0) return;
    const randomWordObj = wordsPool[Math.floor(Math.random() * wordsPool.length)];
    setScrambleWord(randomWordObj);
    setScrambleGuess('');
    setGameMsg(null);

    // Scramble the word letters
    const wordArr = randomWordObj.word.toUpperCase().split('');
    let scrambled = shuffleArray(wordArr).join('');
    while (scrambled === randomWordObj.word.toUpperCase()) {
      scrambled = shuffleArray(wordArr).join('');
    }
    setScrambledText(scrambled);
    setActiveGame('scramble');
  };

  const handleScrambleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!scrambleWord) return;
    if (scrambleGuess.trim().toUpperCase() === scrambleWord.word.toUpperCase()) {
      setScore(prev => prev + 15);
      setGameMsg({ text: `Correct! The word was indeed "${scrambleWord.word}". +15 points`, severity: 'success' });
    } else {
      setGameMsg({ text: `Incorrect guess. Try again!`, severity: 'error' });
    }
  };

  // --- Hangman Game Functions ---
  const startHangman = () => {
    if (wordsPool.length === 0) return;
    // Filter words to make sure they are at least 4 letters and contain only alphabet characters
    const validWords = wordsPool.filter(w => w.word.length >= 4 && /^[a-zA-Z]+$/.test(w.word));
    const target = validWords.length > 0 ? validWords[Math.floor(Math.random() * validWords.length)] : wordsPool[Math.floor(Math.random() * wordsPool.length)];
    
    setHangmanWord(target.word.toUpperCase());
    setHangmanClue(target.definition);
    setGuessedLetters([]);
    setWrongGuessesCount(0);
    setGameMsg(null);
    setActiveGame('hangman');
  };

  const handleHangmanLetterClick = (letter: string) => {
    if (guessedLetters.includes(letter) || wrongGuessesCount >= maxWrongGuesses) return;
    setGuessedLetters(prev => [...prev, letter]);
    
    if (!hangmanWord.includes(letter)) {
      setWrongGuessesCount(prev => prev + 1);
    }
  };

  // Check if player won or lost hangman
  useEffect(() => {
    if (activeGame !== 'hangman' || !hangmanWord) return;

    // Check Win
    const wordLetters = hangmanWord.split('');
    const won = wordLetters.every(char => guessedLetters.includes(char));
    if (won) {
      setScore(prev => prev + 25);
      setGameMsg({ text: `You Win! The word was "${hangmanWord}". +25 points`, severity: 'success' });
    }

    // Check Loss
    if (wrongGuessesCount >= maxWrongGuesses) {
      setGameMsg({ text: `Game Over! The correct word was "${hangmanWord}".`, severity: 'error' });
    }
  }, [guessedLetters, wrongGuessesCount, hangmanWord, activeGame]);

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Back button */}
      <Button 
        startIcon={<ArrowBackIcon />} 
        onClick={() => {
          if (activeGame) {
            setActiveGame(null);
            setGameMsg(null);
          } else {
            navigate('/language-hub');
          }
        }} 
        sx={{ mb: 4 }}
      >
        {activeGame ? 'Exit Game' : 'Language Hub'}
      </Button>

      {/* Landing / Game Selection */}
      {!activeGame && (
        <Box>
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 800, mb: 1.5 }}>
              Vocabulary Games
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              Practice what you learn. Challenge yourself with interactive vocabulary mini-games designed to boost memory and retention.
            </Typography>
          </Box>

          <Grid container spacing={4} sx={{ maxWidth: 900, mx: 'auto' }}>
            {/* Word Match */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', borderRadius: 4, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.01)' }}>
                <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, mb: 2, color: '#10b981' }}>
                    Word Match
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 4, flexGrow: 1 }}>
                    Match definitions to their corresponding vocabulary words.
                  </Typography>
                  <Button variant="contained" color="success" fullWidth onClick={startWordMatch}>
                    Play Now
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Word Scramble */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', borderRadius: 4, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.01)' }}>
                <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, mb: 2, color: '#6366f1' }}>
                    Word Scramble
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 4, flexGrow: 1 }}>
                    Unscramble the letters to form correct vocabulary words.
                  </Typography>
                  <Button variant="contained" color="primary" fullWidth onClick={startWordScramble}>
                    Play Now
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Hangman */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', borderRadius: 4, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.01)' }}>
                <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, mb: 2, color: '#f59e0b' }}>
                    Word Guess
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 4, flexGrow: 1 }}>
                    Find the mystery word by guessing letters before running out of attempts.
                  </Typography>
                  <Button variant="contained" color="warning" fullWidth onClick={startHangman}>
                    Play Now
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Word Match Gameplay */}
      {activeGame === 'match' && (
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>Word Match Game</Typography>
            <Typography variant="h6" color="success.main">Score: {score}</Typography>
          </Box>

          {gameMsg && <Alert severity={gameMsg.severity} sx={{ mb: 4 }} icon={gameMsg.severity === 'success' ? <CheckCircleIcon /> : <CancelIcon />}>{gameMsg.text}</Alert>}

          <Grid container spacing={4}>
            {/* Words list */}
            <Grid item xs={12} sm={6}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Words</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {matchQuestions.map((q) => {
                  const isMatched = matchedPairs.includes(q.word);
                  return (
                    <Button
                      key={q._id}
                      variant={isMatched ? 'contained' : selectedWord === q.word ? 'contained' : 'outlined'}
                      color={isMatched ? 'success' : selectedWord === q.word ? 'primary' : 'inherit'}
                      disabled={isMatched}
                      onClick={() => handleMatchWordClick(q.word)}
                      sx={{ py: 2, justifyContent: 'flex-start', textTransform: 'none', fontWeight: 600 }}
                    >
                      {q.word}
                    </Button>
                  );
                })}
              </Box>
            </Grid>

            {/* Definitions list */}
            <Grid item xs={12} sm={6}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Definitions</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {matchAnswers.map((a) => {
                  const correspondingWord = matchQuestions.find(q => q.definition === a.definition)?.word;
                  const isMatched = matchedPairs.includes(correspondingWord || '');
                  return (
                    <Button
                      key={a._id}
                      variant={isMatched ? 'contained' : selectedDef === a.definition ? 'contained' : 'outlined'}
                      color={isMatched ? 'success' : selectedDef === a.definition ? 'primary' : 'inherit'}
                      disabled={isMatched}
                      onClick={() => handleMatchDefClick(a.definition)}
                      sx={{ py: 2, textAlign: 'left', textTransform: 'none', lineHeight: 1.4 }}
                    >
                      {a.definition}
                    </Button>
                  );
                })}
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: 5, textAlign: 'center' }}>
            <Button startIcon={<ReplayIcon />} variant="outlined" onClick={startWordMatch}>
              Reset Game
            </Button>
          </Box>
        </Box>
      )}

      {/* Word Scramble Gameplay */}
      {activeGame === 'scramble' && scrambleWord && (
        <Box sx={{ maxWidth: 600, mx: 'auto', textAlign: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>Word Scramble</Typography>
            <Typography variant="h6" color="primary.main">Score: {score}</Typography>
          </Box>

          {gameMsg && <Alert severity={gameMsg.severity} sx={{ mb: 4 }}>{gameMsg.text}</Alert>}

          <Card sx={{ p: 4, borderRadius: 4, mb: 4, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <CardContent>
              <Typography variant="h3" sx={{ letterSpacing: 6, fontWeight: 800, color: 'primary.light', mb: 3 }}>
                {scrambledText}
              </Typography>
              <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 4, color: 'text.secondary' }}>
                Clue: "{scrambleWord.definition}"
              </Typography>

              <form onSubmit={handleScrambleSubmit}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Enter your guess..."
                    value={scrambleGuess}
                    onChange={(e) => setScrambleGuess(e.target.value)}
                  />
                  <Button type="submit" variant="contained" color="primary" sx={{ px: 4 }}>
                    Submit
                  </Button>
                </Box>
              </form>
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button startIcon={<ReplayIcon />} variant="outlined" onClick={startWordScramble}>
              Next Word
            </Button>
          </Box>
        </Box>
      )}

      {/* Hangman Gameplay */}
      {activeGame === 'hangman' && hangmanWord && (
        <Box sx={{ maxWidth: 600, mx: 'auto', textAlign: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>Word Guess Game</Typography>
            <Typography variant="h6" color="warning.main">Score: {score}</Typography>
          </Box>

          {gameMsg && <Alert severity={gameMsg.severity} sx={{ mb: 4 }}>{gameMsg.text}</Alert>}

          {/* Attempts left */}
          <Typography variant="h6" color="error.main" sx={{ mb: 3, fontWeight: 700 }}>
            Remaining Errors: {maxWrongGuesses - wrongGuessesCount} / {maxWrongGuesses}
          </Typography>

          {/* Word display (underscores / letters) */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mb: 4 }}>
            {hangmanWord.split('').map((char, index) => {
              const revealed = guessedLetters.includes(char) || wrongGuessesCount >= maxWrongGuesses;
              return (
                <Box
                  key={index}
                  sx={{
                    width: 40,
                    height: 50,
                    borderBottom: '4px solid #f59e0b',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.8rem',
                    fontWeight: 800,
                    color: revealed ? 'warning.light' : 'transparent'
                  }}
                >
                  {char}
                </Box>
              );
            })}
          </Box>

          {/* Clue card */}
          <Card sx={{ p: 3, borderRadius: 3, mb: 4, background: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.05)' }}>
            <Typography variant="body2" sx={{ textTransform: 'uppercase', letterSpacing: 1, color: 'text.secondary', mb: 1, fontWeight: 600 }}>
              Clue / Definition
            </Typography>
            <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
              "{hangmanClue}"
            </Typography>
          </Card>

          {/* Letter buttons keyboard */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mb: 4 }}>
            {'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map((letter) => {
              const isUsed = guessedLetters.includes(letter);
              return (
                <Button
                  key={letter}
                  variant={isUsed ? 'contained' : 'outlined'}
                  color={isUsed ? 'inherit' : 'warning'}
                  disabled={isUsed || wrongGuessesCount >= maxWrongGuesses}
                  onClick={() => handleHangmanLetterClick(letter)}
                  sx={{ minWidth: 40, height: 40, p: 0, fontWeight: 700 }}
                >
                  {letter}
                </Button>
              );
            })}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button startIcon={<ReplayIcon />} variant="outlined" onClick={startHangman}>
              Next Mystery Word
            </Button>
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default VocabularyGames;
